/* ──────────────────────────────────────────────
 *  Champs personnalisés (questionnaire du pro) — helpers purs partagés par le
 *  tunnel classique (CheckoutStep) et le tunnel course (RideTunnel).
 * ────────────────────────────────────────────── */

import {
  isBookingPerimeterApplicable,
  type BookingCustomFieldAnswerObject,
  type BookingCustomFieldPublicObject,
} from '@cfixe-monorepo/shared';
import type { BookingCustomField, CustomFieldAnswer, CustomFieldType } from '../types';

/** Champ public du backend → forme du tunnel (type en minuscules, options = libellés). */
export function mapPublicCustomField(field: BookingCustomFieldPublicObject): BookingCustomField {
  return {
    custom_field_id: field.booking_custom_field_id,
    label: field.label,
    type: field.type.toLowerCase() as CustomFieldType,
    required: field.required,
    placeholder: field.placeholder,
    options: field.options?.map((o): string => o.label),
    min: field.min_value,
    max: field.max_value,
    prestation_ids: field.prestation_ids,
    // Absent = backend antérieur à la portée par lieu : aucune contrainte (comportement historique).
    pro_place_ids: field.pro_place_ids ?? [],
    sort_order: field.sort_order,
  };
}

/** Réservation en cours, telle que la voient les champs : prestations ET lieux réservés. */
export interface BookedCustomFieldScope {
  prestationIds: ReadonlySet<number>;
  placeIds: ReadonlySet<number>;
}

/**
 * Aucun lieu réservé (course VTC : pas de lieu) — référence stable pour les `useMemo` des appelants,
 * figée : un tableau partagé au niveau du module ne doit pas pouvoir être muté d'un tunnel à l'autre.
 */
export const NO_BOOKED_PLACE_IDS: readonly number[] = Object.freeze([] as number[]);

/**
 * Champs proposés pour la réservation en cours (règle partagée isBookingPerimeterApplicable : les
 * dimensions prestation ET lieu se CUMULENT, une dimension vide ne contraint pas ; une réservation
 * sans lieu — course VTC — ne satisfait jamais un champ restreint à des lieux), triés par `sort_order`.
 */
export function filterVisibleCustomFields(
  fields: BookingCustomField[],
  booked: BookedCustomFieldScope,
): BookingCustomField[] {
  const bookedScope = { prestation_ids: booked.prestationIds, pro_place_ids: booked.placeIds };
  return fields
    .filter((f): boolean => isBookingPerimeterApplicable(f, bookedScope))
    .sort((a, b): number => a.sort_order - b.sort_order);
}

/** Réponse absente pour un champ requis : rien saisi, chaîne vide ou case non cochée. */
export function isCustomFieldAnswerMissing(answer: CustomFieldAnswer | undefined): boolean {
  return !answer || answer.value === '' || answer.value === false;
}

/** Remplace (ou ajoute) la réponse du champ `fieldId`. */
export function upsertCustomFieldAnswer(
  answers: CustomFieldAnswer[],
  fieldId: number,
  value: string | number | boolean,
): CustomFieldAnswer[] {
  const index = answers.findIndex((a): boolean => a.custom_field_id === fieldId);
  if (index < 0) return [...answers, { custom_field_id: fieldId, value }];
  const updated = [...answers];
  updated[index] = { custom_field_id: fieldId, value };
  return updated;
}

/**
 * Réponses restreintes aux champs donnés (typiquement les champs visibles) — une réponse saisie
 * pour un champ qui ne s'applique plus (formule changée) n'est pas envoyée — sans les valeurs
 * vides (champ effacé après saisie).
 */
export function answersForFields(answers: CustomFieldAnswer[], fields: BookingCustomField[]): CustomFieldAnswer[] {
  const fieldIds = new Set(fields.map((f): number => f.custom_field_id));
  return answers.filter((a): boolean => fieldIds.has(a.custom_field_id) && a.value !== '');
}

/**
 * Réponses du tunnel → forme API (`BookingCustomFieldAnswerSchema`). Les valeurs sont sérialisées
 * en chaîne (colonne `text`) — convention par type :
 *   - TEXT/TEXTAREA/SELECT/DATE → chaîne telle quelle (DATE = ISO yyyy-mm-dd)
 *   - NUMBER                    → nombre en chaîne ("42")
 *   - CHECKBOX                  → "true" / "false"
 * Les lecteurs de la réponse la parsent selon le `type` du champ.
 */
export function serializeCustomFieldAnswers(answers: CustomFieldAnswer[]): BookingCustomFieldAnswerObject[] {
  return answers.map(
    (a): BookingCustomFieldAnswerObject => ({ booking_custom_field_id: a.custom_field_id, value: String(a.value) }),
  );
}
